import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const NAV_LINKS = [
  { label: "Inicio", href: "#hero" },
  { label: "Nosotros", href: "#nosotros" },
  { label: "Restaurante", href: "#restaurante" },
  { label: "Menú", href: "#menu" },
  { label: "Menú Completo", href: "https://drive.google.com/file/d/1mple6-Br5agmk7h8X32byPCPLAjHXIET/view", external: true },
  { label: "Hospedaje", href: "#hospedaje" },
  { label: "Galería", href: "#galeria" },
  { label: "Contacto", href: "#contacto" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FAF7F2]/85 border-b border-[rgba(45,42,38,0.05)] shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 md:px-12 flex items-center justify-between h-16 md:h-20">
        <a
          href="#hero"
          data-testid="header-logo"
          className={`font-heading text-2xl md:text-3xl font-semibold tracking-tight transition-colors duration-300 ${
            scrolled ? "text-[#2D2A26]" : "text-[#FAF7F2]"
          }`}
        >
          Suchimex
        </a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              {...(link.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`overline text-[0.7rem] transition-colors duration-300 hover:opacity-70 ${
                scrolled ? "text-[#5C5750]" : "text-[#FAF7F2]/90"
              }`}
            >
              {link.label}
            </a>
          ))}
          <a
            href="#reservar"
            data-testid="nav-cta-reservar"
            className="overline text-[0.7rem] bg-[#3F4F34] text-[#FAF7F2] px-6 py-2.5 hover:bg-[#2B3822] transition-all duration-300 hover:-translate-y-0.5"
          >
            Reservar
          </a>
        </nav>

        {/* Mobile hamburger */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`lg:hidden transition-colors ${
            scrolled ? "text-[#2D2A26]" : "text-[#FAF7F2]"
          }`}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#FAF7F2] border-t border-[#D4C3A3]/30 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  {...(link.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="overline text-[0.7rem] text-[#5C5750] py-2 border-b border-[#D4C3A3]/20"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="/reservar"
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-nav-cta-reservar"
                className="overline text-[0.7rem] bg-[#3F4F34] text-[#FAF7F2] px-6 py-3 text-center mt-2"
              >
                Reservar
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
